import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import router from '@/router'

/**
 * 创建axios实例
 */
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api', // API基础URL
  timeout: 15000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

/**
 * 请求拦截器
 * 在发送请求之前做些什么
 */
service.interceptors.request.use(
  config => {
    // 从store中获取token
    const token = store.getters.token
    if (token) {
      // 设置认证头
      config.headers['Authorization'] = `Bearer ${token}`
    }
    
    // 如果是 FormData，删除默认的 Content-Type，让浏览器自动设置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器
 * 对响应数据做点什么
 */
service.interceptors.response.use(
  response => {
    const res = response.data
    
    // 如果返回的状态码为200，说明接口请求成功，可以正常拿到数据
    if (res.code === 200) {
      // code=200时不使用message字段显示成功提示
      // message字段仅在code!=200时用于错误提示
      
      // 直接返回data部分
      return res.data
    }
    
    // 401: 登录态失效
    if (res.code === 401) {
      if (response.config.showErrorMessage !== false) {
        Message.error(res.message || '登录已过期，请重新登录')
        
        // 播放错误音效
        if (window.Vue && window.Vue.prototype.$audio) {
          window.Vue.prototype.$audio.playError()
        }
      }
      
      // 清除本地存储的用户信息
      store.dispatch('logout')
      
      // 安全的路由跳转
      if (router.currentRoute.path !== '/login') {
        router.replace('/login').catch(err => {
          // 忽略导航重复错误
          if (err.name !== 'NavigationDuplicated') {
            console.error('路由跳转错误:', err)
          }
        })
      }
      
      return Promise.reject(new Error(res.message || '登录已过期'))
    }
    
    // 403: 权限不足
    if (res.code === 403) {
      if (response.config.showErrorMessage !== false) {
        Message.error(res.message || '权限不足，无法访问')
        
        // 播放错误音效
        if (window.Vue && window.Vue.prototype.$audio) {
          window.Vue.prototype.$audio.playError()
        }
      }
      
      return Promise.reject(new Error(res.message || '权限不足'))
    }
    
    // 500: 请求失败
    if (res.code === 500) {
      if (response.config.showErrorMessage !== false) {
        Message.error(res.message || '服务器内部错误')
        
        // 播放错误音效
        if (window.Vue && window.Vue.prototype.$audio) {
          window.Vue.prototype.$audio.playError()
        }
      }
      
      return Promise.reject(new Error(res.message || '服务器内部错误'))
    }
    
    // 其他错误码
    const errorMessage = res.message || '请求失败'
    if (response.config.showErrorMessage !== false) {
      Message.error(errorMessage)
      
      // 播放错误音效
      if (window.Vue && window.Vue.prototype.$audio) {
        window.Vue.prototype.$audio.playError()
      }
    }
    
    return Promise.reject(new Error(errorMessage))
  },
  error => {
    console.error('响应错误:', error)
    
    // 检查是否禁用错误提示
    // 注意：error.config 可能不存在（例如请求取消）
    const showErrorMessage = error.config ? error.config.showErrorMessage !== false : true
    
    let message = '网络错误'
    
    if (error.response) {
      // 服务器返回了错误状态码
      switch (error.response.status) {
        case 400:
          message = '请求参数错误'
          break
        case 401:
          message = '未授权，请重新登录'
          // 清除本地存储的用户信息
          store.dispatch('logout')
          // 安全的路由跳转
          if (router.currentRoute.path !== '/login') {
            router.replace('/login').catch(err => {
              // 忽略导航重复错误
              if (err.name !== 'NavigationDuplicated') {
                console.error('路由跳转错误:', err)
              }
            })
          }
          break
        case 403:
          message = '权限不足'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        case 502:
          message = '网关错误'
          break
        case 503:
          message = '服务不可用'
          break
        case 504:
          message = '网关超时'
          break
        default:
          message = `连接错误${error.response.status}`
      }
    } else if (error.code === 'ECONNABORTED') {
      message = '请求超时'
    } else if (error.message.includes('Network Error')) {
      message = '网络连接异常'
    }
    
    if (showErrorMessage) {
      Message.error(message)
      
      // 播放错误音效
      if (window.Vue && window.Vue.prototype.$audio) {
        window.Vue.prototype.$audio.playError()
      }
    }
    
    return Promise.reject(error)
  }
)

export default service